import React, { useState, useEffect } from "react";
import Papa from "papaparse";
import dayjs from "dayjs";
import "dayjs/locale/pt";
import "dayjs/locale/de";
import "../App.css";
import pt from "../i18n/pt.json";
import en from "../i18n/en.json";
import de from "../i18n/de.json";
import { useLanguage } from "../context/LanguageContext";
import MonthlyView from "../components/MonthlyView";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isoWeek from "dayjs/plugin/isoWeek";
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isoWeek);

const API_BASE_URL = "https://scheduleapp-backend.whitemoss-45cf5382.westeurope.azurecontainerapps.io";

// Convert a string to a consistent hex color
function stringToColor(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const c = (hash & 0x00ffffff).toString(16).toUpperCase();
  return "#" + "000000".substring(0, 6 - c.length) + c;
}

// Convert hex (#AABBCC) to semi-transparent RGBA
function hexToRgba(hex, alpha) {
  const cleanHex = hex.replace(/^#/, "");
  let r, g, b;
  if (cleanHex.length === 3) {
    r = parseInt(cleanHex.charAt(0) + cleanHex.charAt(0), 16);
    g = parseInt(cleanHex.charAt(1) + cleanHex.charAt(1), 16);
    b = parseInt(cleanHex.charAt(2) + cleanHex.charAt(2), 16);
  } else {
    r = parseInt(cleanHex.substring(0, 2), 16);
    g = parseInt(cleanHex.substring(2, 4), 16);
    b = parseInt(cleanHex.substring(4, 6), 16);
  }
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function parseDate(str) {
    return dayjs(str, "MM/DD/YYYY"); // no .toDate()
  }

// Helper to format "19h" into "19h00"
function formatHour(str) {
  let numeric = str.replace("h", "");
  let val = parseFloat(numeric);
  if (isNaN(val)) {
    return str;
  }
  const hours = Math.floor(val);
  const fraction = val - hours;
  const minutes = Math.round(fraction * 60);
  return `${hours}h${String(minutes).padStart(2, "0")}`;
}

function CalendarPage() {
const { t, lang } = useLanguage();

//day names, Monday-based:
const PORTUGUESE_DAYS = [
  t("calendar.monday"),
  t("calendar.tuesday"),
  t("calendar.wednesday"),
  t("calendar.thursday"),
  t("calendar.friday"),
  t("calendar.saturday"),
  t("calendar.sunday"),
];

  // 1) States for file selection & data
const [calendarData, setCalendarData] = useState([]);
const [availableFiles, setAvailableFiles] = useState([]);
const [selectedFile, setSelectedFile] = useState("");

// 2) States for filters
const [filterType, setFilterType] = useState("empregados");
const [selectedValue, setSelectedValue] = useState(null);
const [uniqueEmployees, setUniqueEmployees] = useState([]);
const [uniqueClients, setUniqueClients] = useState([]);

// 3) Week navigation
const [selectedDate, setSelectedDate] = useState(new Date());

const [weeklyHoursData, setWeeklyHoursData] = useState([]);

// 4) Layout constants
const DAY_BODY_VH = 65;
const HOURS_PER_DAY = 24;
const HOUR_VH = DAY_BODY_VH / HOURS_PER_DAY;

// 5) Hover tooltip state
const [hoveredSession, setHoveredSession] = useState(null);

const [viewType, setViewType] = useState("weekly"); // 🆕 New view state

useEffect(() => {
  dayjs.locale(lang || "en"); // use `lang` from context
}, [lang]);

// ✅ Fetch Available CSV Files from Backend
useEffect(() => {
  async function fetchFiles() {
    try {
      const res = await fetch(`${API_BASE_URL}/api/files`);
      if (!res.ok) throw new Error("Failed to fetch file list");

      const data = await res.json();
      if (!data.files || data.files.length === 0) throw new Error("No files found");

      setAvailableFiles(data.files);
      setSelectedFile(data.files[0] || ""); // Auto-select first file if available
    } catch (err) {
      console.error("Error fetching file list:", err);
      setAvailableFiles([]); // Ensure we don't have a stale state
    }
  }
  fetchFiles();
}, []);

// ✅ Load & Parse CSV when File is Selected
useEffect(() => {
  if (!selectedFile) return;

  const csvPath = `${API_BASE_URL}/api/download/${selectedFile}`;
  console.log(`Loading CSV: ${csvPath}`);

  Papa.parse(csvPath, {
    download: true,
    header: true,
    skipEmptyLines: true,
    complete: (results) => {
      if (!results.data || !Array.isArray(results.data)) {
        console.warn("No valid data found in CSV");
        return;
      }

      const finalData = results.data
        .map((row) => {
          const baseDate = row["date"] ? parseDate(row["date"]) : null;
          if (!baseDate || !baseDate.isValid()) return null;

          const session = {
            client_id: row["client_id"] || "",
            session_id: row["session_id"] || "",
            employee_name: row["employee_name"] || "",
          };

          const startStr = (row["start"] || "0h").replace("h", "");
          const endStr = (row["end"] || "0h").replace("h", "");
          const startH = parseFloat(startStr) || 0;
          const endH = parseFloat(endStr) || 0;

          if (endH <= startH) {
            const nextDay = baseDate.add(1, "day");
            return [
              { ...session, date: baseDate, start: `${startH}h`, end: "24h" },
              { ...session, date: nextDay, start: "0h", end: `${endH}h` },
            ];
          } else {
            return { ...session, date: baseDate, start: `${startH}h`, end: `${endH}h` };
          }
        })
        .flat()
        .filter(Boolean);

      // ✅ Compute weekly hours
      const hoursData = finalData.map((row) => {
        const date = dayjs(row.date);
        const start = parseFloat(row.start.replace("h", "")) || 0;
        const end = parseFloat(row.end.replace("h", "")) || 0;

        let duration = 0;
        if (end < start) {
          duration = (24 - start) + end;
        } else {
          duration = end - start;
        }

        return {
          employee_name: row.employee_name,
          week: date.isoWeek(),
          duration
        };
      });

      // ✅ Group by employee and week
      const grouped = {};
      hoursData.forEach((row) => {
        const key = `${row.employee_name}-${row.week}`;
        if (!grouped[key]) {
          grouped[key] = { employee_name: row.employee_name, week: row.week, duration: 0 };
        }
        grouped[key].duration += row.duration;
      });

      const weeklySummary = Object.values(grouped).sort((a, b) => {
        if (a.employee_name !== b.employee_name) {
          return a.employee_name.localeCompare(b.employee_name);
        }
        return a.week - b.week;
      });

      setWeeklyHoursData(weeklySummary);
      setCalendarData(finalData);

      const employees = [...new Set(finalData.map((i) => i.employee_name))]
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, "pt-BR", { sensitivity: "base" }));
      const clients = [...new Set(finalData.map((i) => i.client_id))]
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, "pt-BR", { sensitivity: "base" }));

      setUniqueEmployees(employees);
      setUniqueClients(clients);
    },
    error: (err) => {
      console.error("Error parsing CSV:", err);
    },
  });
}, [selectedFile]);

function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

  // 7) Compute Monday-Sunday range for currentWeek
  function getWeekDates(date) {
    const dayjsDate = dayjs(date);
    const dayOfWeek = dayjsDate.day(); // Sunday = 0, Monday = 1, ..., Saturday = 6
    const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = dayjsDate.add(diffToMonday, "day").startOf("day");
    const sunday = monday.add(6, "day");
    return {
      startDate: monday,
      endDate: sunday,
    };
  }

  function handleDateChange(event) {
    setSelectedDate(new Date(event.target.value));
  }

  function getCalendarTitle() {
    if (viewType === "weekly") {
      const { startDate, endDate } = getWeekDates(selectedDate);
      const start = dayjs(startDate).format("D MMM");
      const end = dayjs(endDate).format("D MMM YYYY");

      let title = `${t("calendar.week_of")} ${start} – ${end}`;

      if (filterType === "empregados" && selectedValue) {
        const weekNumber = dayjs(selectedDate).isoWeek();
        const employeeWeek = weeklyHoursData.find(
          (entry) => entry.employee_name === selectedValue && entry.week === weekNumber
        );

        if (employeeWeek) {
          title += ` - ${employeeWeek.duration.toFixed(1)}h`;
        }
      }

      return title;
    } else if (viewType === "daily") {
      return capitalize(dayjs(selectedDate).format("dddd, D MMMM YYYY"));
    } else if (viewType === "monthly") {
      return dayjs(selectedDate).format("MMMM YYYY");
    }
    return "";
  }

  function navigate(offset) {
    setSelectedDate((prev) => {
      const newDate = new Date(prev);
      if (viewType === "daily") {
        newDate.setDate(newDate.getDate() + offset);
      } else if (viewType === "weekly") {
        newDate.setDate(newDate.getDate() + offset * 7);
      } else if (viewType === "monthly") {
        newDate.setMonth(newDate.getMonth() + offset);
      }
      return newDate;
    });
  }

  function getFilteredData() {
    if (!calendarData.length) return [];

    let filtered = calendarData;

    if (viewType === "weekly") {
      const { startDate, endDate } = getWeekDates(selectedDate);
      const start = startDate.startOf("day");
      const end = endDate.endOf("day");

      filtered = filtered.filter((item) => {
        const date = dayjs(item.date);
        return date.isValid() && date.isSameOrAfter(start) && date.isSameOrBefore(end);
      });
    } else if (viewType === "daily") {
      const current = dayjs(selectedDate).startOf("day");
      filtered = filtered.filter((item) => {
        const date = dayjs(item.date);
        return date.isValid() && date.isSame(current, "day");
      });
    } else if (viewType === "monthly") {
      const currentMonth = dayjs(selectedDate).month();
      const currentYear = dayjs(selectedDate).year();
      filtered = filtered.filter((item) => {
        const d = dayjs(item.date);
        return d.isValid() && d.month() === currentMonth && d.year() === currentYear;
      });
    }

    if (selectedValue) {
      filtered =
        filterType === "empregados"
          ? filtered.filter((i) => i.employee_name === selectedValue)
          : filtered.filter((i) => i.client_id === selectedValue);
    }

    return filtered;
  }

  // 9) Render the 7-day calendar
  function renderCalendarGrid() {
    const data = getFilteredData();
    const { startDate, endDate } = getWeekDates(selectedDate);

    const sessionsByDay = [[], [], [], [], [], [], []];

    data.forEach((session) => {
      const dayIdx = session.date ? dayjs(session.date).diff(startDate, "day") : -1;
      if (dayIdx < 0 || dayIdx > 6) return;

      const startHour = parseFloat(session.start.replace("h", "")) || 0;
      const endHour = parseFloat(session.end.replace("h", "")) || 0;

      if (endHour < startHour) {
        sessionsByDay[dayIdx].push({ ...session, startHour, endHour: 24 });
        if (dayIdx < 6) {
          sessionsByDay[dayIdx + 1].push({
            ...session,
            startHour: 0,
            endHour,
          });
        }
      } else {
        sessionsByDay[dayIdx].push({ ...session, startHour, endHour });
      }
    });

    return (
      <div className="calendar-grid">
        {[...Array(7).keys()].map((d) => {
          const columnDate = startDate.add(d, "day");
          const formattedWeekdayName = columnDate.format("dddd");
          const dayHeaderLabel = `${formattedWeekdayName} - ${t("calendar.day")} ${columnDate.date()}/${columnDate.month() + 1}`;

          // Group sessions by identical start/end
          const daySessions = sessionsByDay[d] || [];
          const groupedByTime = {};
          daySessions.forEach((sesh) => {
            const key = `${sesh.startHour}-${sesh.endHour}`;
            if (!groupedByTime[key]) groupedByTime[key] = [];
            groupedByTime[key].push(sesh);
          });

          return (
            <div key={formattedWeekdayName} className="calendar-day">
              <div className="calendar-day-header">{dayHeaderLabel}</div>
              <div className="calendar-day-body">
                {[...Array(HOURS_PER_DAY + 1).keys()].map((hour) => (
                  <div
                    key={hour}
                    className="calendar-hour-line"
                    style={{ height: `${HOUR_VH}vh` }}
                  >
                    <span className="calendar-hour-label">{hour}:00</span>
                  </div>
                ))}

                {/* Render sessions side-by-side */}
                {Object.entries(groupedByTime).map(([timeKey, group]) => {
                  const [startH, endH] = timeKey.split("-").map(Number);
                  const duration = endH - startH;
                  const topVH = startH * HOUR_VH;
                  const heightVH = duration * HOUR_VH;
                  const count = group.length;

                  return group.map((sesh, i) => {
                    const colWidth = 100 / count;
                    const leftPercent = i * colWidth;

                    const baseColor = stringToColor(sesh.employee_name);
                    const bgColor = hexToRgba(baseColor, 0.5);

                    const boxLabel = `${sesh.employee_name}\nID:${sesh.session_id}`;

                    return (
                      <div
                        key={`${timeKey}-${i}`}
                        className="calendar-session"
                        style={{
                          top: `${topVH}vh`,
                          height: `${heightVH}vh`,
                          width: `${colWidth}%`,
                          left: `${leftPercent}%`,
                          backgroundColor: bgColor,
                        }}
                        onMouseEnter={(e) => {
                          setHoveredSession({
                            data: sesh,
                            x: e.clientX,
                            y: e.clientY,
                          });
                        }}
                        onMouseLeave={() => setHoveredSession(null)}
                      >
                        {boxLabel}
                      </div>
                    );
                  });
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  function renderDailyView() {
    const filtered = getFilteredData();
    const currentDate = dayjs(selectedDate).startOf("day");

    // Only sessions for selected day
    const sessions = filtered.filter((item) => {
      return item.date && item.date.isSame(currentDate, "day");
    });

    // Group by overlapping times
    const groupedByTime = {};
    sessions.forEach((sesh) => {
      const startHour = parseFloat(sesh.start.replace("h", "")) || 0;
      const endHour = parseFloat(sesh.end.replace("h", "")) || 0;
      const key = `${startHour}-${endHour}`;
      if (!groupedByTime[key]) groupedByTime[key] = [];
      groupedByTime[key].push({ ...sesh, startHour, endHour });
    });

    return (
      <div className="calendar-grid">
        <div className="calendar-day">
          <div className="calendar-day-header">
            {dayjs(currentDate).format("dddd - D/MM")}
          </div>
          <div className="calendar-day-body">
            {[...Array(HOURS_PER_DAY + 1).keys()].map((hour) => (
              <div
                key={hour}
                className="calendar-hour-line"
                style={{ height: `${HOUR_VH}vh` }}
              >
                <span className="calendar-hour-label">{hour}:00</span>
              </div>
            ))}

            {Object.entries(groupedByTime).map(([timeKey, group]) => {
              const [startH, endH] = timeKey.split("-").map(Number);
              const duration = endH - startH;
              const topVH = startH * HOUR_VH;
              const heightVH = duration * HOUR_VH;
              const count = group.length;

              return group.map((sesh, i) => {
                const colWidth = 100 / count;
                const leftPercent = i * colWidth;
                const baseColor = stringToColor(sesh.employee_name);
                const bgColor = hexToRgba(baseColor, 0.5);
                const boxLabel = `${sesh.employee_name}\nID:${sesh.session_id}`;

                return (
                  <div
                    key={`${timeKey}-${i}`}
                    className="calendar-session"
                    style={{
                      top: `${topVH}vh`,
                      height: `${heightVH}vh`,
                      width: `${colWidth}%`,
                      left: `${leftPercent}%`,
                      backgroundColor: bgColor,
                    }}
                    onMouseEnter={(e) => {
                      setHoveredSession({
                        data: sesh,
                        x: e.clientX,
                        y: e.clientY,
                      });
                    }}
                    onMouseLeave={() => setHoveredSession(null)}
                  >
                    {boxLabel}
                  </div>
                );
              });
            })}
          </div>
        </div>
      </div>
    );
  }

  // 10) Format the "bottom bar" text
  const filterOptions =
    filterType === "empregados" ? uniqueEmployees : uniqueClients;

  return (
    <div className="app-container">
      {/* TOP BAR */}
      <div className="top-bar">
        <div className="filter-group">
          <label>{t("calendar.file")}: </label>
          <select
            className="select-input"
            value={selectedFile}
            onChange={(e) => setSelectedFile(e.target.value)}
          >
            {availableFiles.map((file) => (
              <option key={file} value={file}>
                {file}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>{t("calendar.view")}: </label>
          <select
            className="select-input"
            value={viewType}
            onChange={(e) => setViewType(e.target.value)}
          >
            <option value="daily">{t("calendar.daily")}</option>
            <option value="weekly">{t("calendar.weekly")}</option>
            <option value="monthly">{t("calendar.monthly")}</option>
          </select>
        </div>

        <div className="filter-group">
          <label>{t("calendar.date")}: </label>
          <input
            type="date"
            className="select-input"
            style={{ padding: "0.2rem"}}
            value={dayjs(selectedDate).format("YYYY-MM-DD")}
            onChange={handleDateChange}
          />
        </div>

        <div className="filter-group">
          <label>{t("calendar.filter")}: </label>
          <select
            className="select-input"
            value={filterType}
            onChange={(e) => {
              setFilterType(e.target.value);
              setSelectedValue(null);
            }}
          >
            <option value="empregados">{t("calendar.employees")}</option>
            <option value="clientes">{t("calendar.services")}</option>
          </select>
        </div>

        <div className="filter-group">
          <label>
            {filterType === "empregados" ? `${t("calendar.employee")}:` : `${t("calendar.service")}:`}
          </label>
          <select
            className="select-input"
            value={selectedValue || ""}
            onChange={(e) => setSelectedValue(e.target.value || null)}
          >
            <option value="">{t("calendar.all")}</option>
            {filterOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* CALENDAR */}
      {viewType !== "daily" && (
        <div className="calendar-title">
          <h2>{getCalendarTitle()}</h2>
        </div>
      )}
      <div className="calendar-container">
        {calendarData.length === 0 ? (
          <p className="loading-text">{t("calendar.loading_calendar")}</p>
        ) : viewType === "monthly" ? (
          <MonthlyView
            calendarData={calendarData}
            selectedDate={selectedDate}
            employees={uniqueEmployees}
            t={t}
          />
        ) : viewType === "daily" ? (
          renderDailyView()
        ) : (
          renderCalendarGrid()
        )}
      </div>

      {/* TOOLTIP */}
      {hoveredSession && (
        <div
          className="session-tooltip"
          style={{
            position: "fixed",
            top: hoveredSession.y + 10,
            left: hoveredSession.x + 10,
            backgroundColor: "#fff",
            border: "1px solid #333",
            padding: "8px",
            borderRadius: "4px",
            zIndex: 9999,
          }}
        >
          {renderTooltipContent(hoveredSession.data, t, lang)}
        </div>
      )}

      {/* BOTTOM BAR */}
      <div className="bottom-bar">
        <button className="nav-button" onClick={() => navigate(-1)} style={{ position: "absolute", left: "1rem" }}>{t("calendar.previous")}</button>
        <button className="nav-button" onClick={() => navigate(1)} style={{ position: "absolute", right: "1rem" }}>{t("calendar.next")}</button>
      </div>
    </div>
  );
}

function renderTooltipContent(session, t, lang) {
  const dateStr = session.date
  ? session.date.locale(lang).format("D MMMM YYYY")
  : "(no date)";

  const startFormatted = formatHour(session.start);
  const endFormatted = formatHour(session.end);

  return (
    <div style={{ fontSize: "14px", lineHeight: "1.4" }}>
      <p>
        <strong>{t("calendar.date")}:</strong> {dateStr}
      </p>
      <p>
        <strong>{t("calendar.client")}:</strong> {session.client_id}
      </p>
      <p>
        <strong>{t("data.service_id")}:</strong> {session.session_id}
      </p>
      <p>
        <strong>{t("calendar.employee")}:</strong> {session.employee_name}
      </p>
      <p>
        <strong>{t("data.start")}:</strong> {startFormatted}
      </p>
      <p>
        <strong>{t("data.end")}:</strong> {endFormatted}
      </p>
    </div>
  );
}

export default CalendarPage;
